import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { search } from '../../actions/search';
import { track } from '../../actions/tracking';

import { useNavigate } from 'react-router-dom';
import { asHtml } from '../../helpers/markdown';
import QueryFilter from '../../helpers/query-filter';
import { getSearchManufacturePackaging } from '../../selectors/branding';
import { GuaranteeEvents } from '../../shared/tracking';
import Checklist from '../../styled-components/components/Checklist';
import Modal from '../../styled-components/components/Modal';
import { StackMedium } from '../../styled-components/components/Stack';
import { getQueryFilter } from '../../utils/reselectors';

const hasManufacturePackage = (queryFilter: QueryFilter) => {
  if (queryFilter && queryFilter.get('hasManufacturerPackaging').length === 1) {
    return queryFilter.get('hasManufacturerPackaging')[0] === 'true';
  }

  return false;
};

const ManufacturePackaging = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const manufacturePackage = useSelector(getSearchManufacturePackaging);
  const queryFilter = useSelector(getQueryFilter);

  const { onSearch, trackEvent } = bindActionCreators(
    {
      onSearch: search,
      trackEvent: track,
    },
    dispatch,
  );

  const [checked, setChecked] = useState(hasManufacturePackage(queryFilter));
  const [modal, setModal] = useState(false);

  useEffect(() => {
    if (hasManufacturePackage(queryFilter) && !checked) {
      setChecked(true);
    } else if (!hasManufacturePackage(queryFilter) && checked) {
      setChecked(false);
    }
  }, [queryFilter]);

  if (!manufacturePackage) return null;

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(e.currentTarget.checked);
    let qf = new QueryFilter(queryFilter.toString());
    if (e.currentTarget.checked) {
      qf = qf.add('hasManufacturerPackaging', true);
    } else {
      qf = qf.remove('hasManufacturerPackaging');
    }

    onSearch(qf);
    trackEvent(GuaranteeEvents.MANUFACTURER_PACKAGE_BANNER_CLICK, {
      information: {
        manufacturePackageName: manufacturePackage.name,
      },
    });
    navigate(`/sok${qf.toQueryURL()}`, { replace: true });
  };

  const onOpenModal = () => {
    trackEvent(GuaranteeEvents.MANUFACTURER_PACKAGE_MODAL_SHOW, {
      information: {
        manufacturePackageName: manufacturePackage.name,
      },
    });
    setModal(true);
  };

  const onWebsiteClick = () =>
    trackEvent(GuaranteeEvents.MANUFACTURER_PACKAGE_MODAL_READ_MORE, {
      information: {
        manufacturePackageName: manufacturePackage.name,
      },
    });

  const { logo, name, description, uspTitle, uspList, website } = manufacturePackage;

  return (
    <section className="page-section page-section-thin">
      {modal && (
        <Modal heading={name} onClose={() => setModal(false)}>
          {logo && (
            <StackMedium>
              <img src={`${logo}?w=200&h=32`} alt={name} className="l-block" />
            </StackMedium>
          )}
          {description && (
            <StackMedium>
              <div data-am-content="" dangerouslySetInnerHTML={{ __html: asHtml(description) }} />
            </StackMedium>
          )}
          {uspList && !!uspList.length && (
            <StackMedium>
              {uspTitle && <div className="font-medium m-b-half">{uspTitle}</div>}
              <Checklist items={uspList} />
            </StackMedium>
          )}
          {website && (
            <StackMedium>
              <a
                data-am-link=""
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onWebsiteClick}
              >
                {t('Read more about this', { ns: 'common', name })}
                <i className="icon-link-external m-l-mini" />
              </a>
            </StackMedium>
          )}
        </Modal>
      )}
      <div data-am-togglebar="">
        <div className="toggle-bar-header">
          <div className="toggle-bar-header-section">
            <div data-am-switch="block">
              <input
                type="checkbox"
                className="checkbox"
                id="manufacture-package-switch"
                onChange={onChange}
                checked={checked}
              />
              <label
                htmlFor="manufacture-package-switch"
                aria-label="manufacture-package-switch"
                className="switch"
              />
            </div>
          </div>
          <div className="toggle-bar-header-section">
            {logo && (
              <img
                src={`${logo}?w=200&h=32`}
                srcSet={`${logo}?w=400&h=64 2x`}
                alt={name}
                className="toggle-bar-logo"
              />
            )}
          </div>
        </div>
        <div className="toggle-bar-body">
          <div className="toggle-bar-text">
            {description && asHtml(description.substring(0, 200)).replace(/<\/?[^>]+(>|$)/g, '')}
          </div>
          <div className="toggle-bar-link">
            <div data-am-link="" onClick={onOpenModal}>
              {t('Read more', { ns: 'common' })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ManufacturePackaging;
